import React, { useMemo } from 'react';

import { ListerItem, Badge, Label, Notification, Card, Link, type DataItemProps } from '@ergeon/core-components';
import { ArrowTopRightOnSquareIcon } from '@ergeon/icons/hero/micro';

import { ROUTES } from '../../routes';
import { CallInfoAggregated } from '../../types';
import { formatSecondsToTimeString, getAdminLink } from '../../utils';
import { formatPhoneNumber, formatDateTime } from '../Customers/utils/formatUtils';

import { useCallControlButtons } from './CallControlButtons';
import { useHistoricalCallButtons } from './HistoricalCallButtons';

type CallInfoProps = {
  data: CallInfoAggregated;
  transferFeedback?: {type: 'success' | 'error'; message: string} | null;
};

const capitalizeFirstLetter = (str?: string): string => {
  if (!str) return 'Unknown';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Builds the data items array from aggregated call info.
 * This is the single source of truth for what fields to display.
 */
const buildItems = (data: CallInfoAggregated): DataItemProps[] => {
  const items: DataItemProps[] = [];

  // Customer/Order info (if available)
  if (data.order) {
    const adminUrl = getAdminLink({ app: 'store', model: 'order', id: data.order.id });
    items.push({
      title: 'Order',
      value: (
        <Link
          className="flex items-center gap-1"
          href={adminUrl}
          rel="noreferrer"
          target="_blank">
          #{data.order.id}
          <ArrowTopRightOnSquareIcon className="size-4 shrink-0" />
        </Link>
      ),
    });
  }

  if (data.customer) {
    items.push({
      title: 'Customer',
      value: (
        <Link href={ROUTES.customer.$buildPath({ params: { customerId: data.customer.id } })}>
          {data.customer.fullName}
        </Link>
      ),
    });

    if (data.customer.primaryPhone) {
      items.push({
        title: 'Phone',
        value: formatPhoneNumber(data.customer.primaryPhone),
      });
    }

    if (data.customer.primaryEmail) {
      items.push({
        title: 'Email',
        value: data.customer.primaryEmail,
      });
    }
  }

  // Core call info
  items.push({
    title: 'Direction',
    value: capitalizeFirstLetter(data.direction),
  });

  items.push({
    title: 'Status',
    value: <Badge flavor="primary" value={capitalizeFirstLetter(data.status)} />,
  });

  if (data.createdAt) {
    items.push({
      title: 'Date & Time',
      value: formatDateTime(data.createdAt),
    });
  }

  if (data.duration !== undefined) {
    items.push({
      title: 'Duration',
      value: formatSecondsToTimeString(data.duration),
    });
  }

  // Warm transfer info (for active calls)
  if (data.hasWarmTransfer && data.warmTransferAgent) {
    items.push({
      title: 'Warm Transfer',
      value: <Badge flavor="primary" value="Agent Invitation" />,
    });
    items.push({
      title: 'Inviting Agent',
      value: data.warmTransferAgent,
    });
  }

  // Caller ID info for outbound calls
  if (data.fromNumber) {
    const phoneNumber = formatPhoneNumber(data.fromNumber.callerId);
    const displayValue = data.fromNumber.lineName ?
      `${data.fromNumber.lineName} - ${phoneNumber}` :
      phoneNumber;

    items.push({
      title: 'From Number',
      value: displayValue,
    });
  }

  return items;
};

/**
 * CallInfo component - displays call information in a card.
 *
 * This is a presentational component that receives aggregated call data.
 * The parent is responsible for:
 * - Fetching call data from various sources
 * - Using CallInfoBuilder to create CallInfoAggregated
 */
const CallInfo: React.FC<CallInfoProps> = ({
  data,
  transferFeedback,
}) => {
  const items = useMemo(() => buildItems(data), [data]);

  const activeCallButtons = useCallControlButtons();

  const historicalCallButtons = useHistoricalCallButtons({ data });

  const isActiveCall = data.isActive && data.status !== 'closed';
  const buttons = isActiveCall ? activeCallButtons : historicalCallButtons;

  // Determine the title
  const callTitle = data.contactName || formatPhoneNumber(data.phoneNumber);

  return (
    <Card className="relative flex flex-col gap-3 p-4 w-full" flavor="base" zIndex="1">
      <Label>Call Info</Label>
      {data.hasWarmTransfer && data.warmTransferAgent && (
        <Notification mode="embed" type="Information">
          <strong>Warm Transfer Call:</strong> You are being invited to join this call by agent{' '}
          {data.warmTransferAgent}
        </Notification>
      )}
      <ListerItem
        aria-label="Call information details"
        buttons={buttons}
        items={items}
        title={callTitle} />
      {transferFeedback && (
        <Notification mode="embed" type={transferFeedback.type === 'success' ? 'Success' : 'Error'}>
          {transferFeedback.message}
        </Notification>
      )}
    </Card>
  );
};

export default CallInfo;
